/**
 * Canonical per-PNR involuntary-change wire contract — the shape the integrator
 * (`schedule-change-integration`) emits per PNR and that the agent
 * (`flight_schedule_change`) consumes verbatim from `SystemEventEnvelope.metadata`.
 *
 * Lives in `packages/domain` so the integrator and the agent share ONE definition
 * instead of copy-pasting it: `SystemEventEnvelope.metadata` is
 * `z.record(z.string(), z.unknown())` (an untyped bag), so typing the payload
 * here is the only place the shape is pinned.
 *
 * Dates stay RAW strings — never `z.date()` / `z.coerce.date()`. The source emits
 * ISO `YYYY-MM-DD HH:MM:SS`; coercing to Date would silently shift the wall-clock
 * time (the voucher-date-fidelity landmine). The string is carried through
 * untouched and parsed only at the display/agent boundary downstream.
 *
 * This is a cross-service wire contract, NOT a persisted Mongo document, so it
 * carries no `_schemaVersion`.
 */
import { z } from 'zod';

/**
 * One flight segment — both the original schedule and the reaccommodated one
 * share this shape. All fields are raw strings (see file header on date fidelity).
 */
export const InvoluntaryChangeSegmentSchema = z.object({
  flightNumber: z.string(),
  departureAirport: z.string(),
  arrivalAirport: z.string(),
  departureTime: z.string(),
  arrivalTime: z.string(),
});
export type InvoluntaryChangeSegment = z.infer<typeof InvoluntaryChangeSegmentSchema>;

/**
 * One leg of a per-PNR involuntary change. `original` is always present; a null
 * `reaccommodated` means the leg was CANCELLED (no replacement flight). Both
 * sides reuse `InvoluntaryChangeSegmentSchema` (raw-string fields; see file
 * header on date fidelity).
 */
export const LegChangeSchema = z.object({
  original: InvoluntaryChangeSegmentSchema,
  reaccommodated: InvoluntaryChangeSegmentSchema.nullable(),
});
export type LegChange = z.infer<typeof LegChangeSchema>;

/**
 * Per-PNR involuntary change — the wire shape the integrator emits and the agent
 * consumes. One event per PNR (localizador), carrying every changed leg.
 */
export const InvoluntaryChangeSchema = z.object({
  localizador: z.string().min(1),
  // The VOLL voucher code resolved by-locator at enrichment time (NOT a source
  // column — the integrator stamps it after the by-locator lookup). Optional +
  // nullable: a voucher with no code, or a legacy event published before this
  // field existed, falls back to `localizador` downstream. Shown to the traveler
  // in the proactive notification.
  voucherCode: z.string().nullable().optional(),
  legs: z.array(LegChangeSchema).min(1),
});
export type InvoluntaryChange = z.infer<typeof InvoluntaryChangeSchema>;
